import React, { useState } from 'react';
import { useMutation } from '@apollo/client';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
} from '@mui/material';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import SmsOutlinedIcon from '@mui/icons-material/SmsOutlined';

import { describeBatchError, forgetBatchData } from '../../batch/batchUpdate';
import { CRM_SET_CONTACT_FLAG } from '../contactDetail.queries';
import { ContactDetail } from '../contactDetail';

export type ConsentField = 'emailOptIn' | 'smsOptIn';

const WORDS: Record<ConsentField, { on: string; off: string; onMeans: string; offMeans: string; icon: React.ReactElement }> = {
  emailOptIn: {
    on: 'Opt in to emails',
    off: 'Unsubscribe from emails',
    onMeans: 'They will receive the organisation\'s emails again, if their address is good.',
    offMeans: 'They will not be sent emails. They are marked unsubscribed today.',
    icon: <MailOutlineIcon />,
  },
  smsOptIn: {
    on: 'Opt in to texts',
    off: 'Stop texts',
    onMeans: 'They can be sent text messages again, if their main number is good.',
    offMeans: 'They will not be sent text messages.',
    icon: <SmsOutlinedIcon />,
  },
};

/** What the dialog sends: a one-person batch update (card 20), so it is recorded and can be undone. */
export const consentChange = (field: ConsentField, value: boolean, note: string) => {
  const trimmed = note.trim();
  return { fields: { [field]: value }, ...(trimmed ? { note: trimmed } : {}) };
};

const ConsentDialog = ({
  contactId,
  field,
  value,
  onClose,
  onDone,
}: {
  contactId: number;
  field: ConsentField;
  value: boolean;
  onClose: () => void;
  onDone: () => void;
}) => {
  const [note, setNote] = useState('');
  const [run, { loading, error }] = useMutation(CRM_SET_CONTACT_FLAG, { update: (cache) => forgetBatchData(cache) });
  const words = WORDS[field];
  // Opening consent needs to say how they gave it, as the API requires.
  const ready = !value || note.trim().length > 0;

  const submit = async () => {
    try {
      await run({ variables: { filter: { ids: [contactId] }, change: consentChange(field, value, note) } });
      onDone();
    } catch {
      // Shown below from `error`.
    }
  };

  return (
    <Dialog open onClose={loading ? undefined : onClose} maxWidth="xs" fullWidth aria-labelledby="crm-consent-title">
      <DialogTitle id="crm-consent-title">{value ? words.on : words.off}</DialogTitle>
      <DialogContent sx={{ display: 'grid', gap: 2 }}>
        <DialogContentText>
          {value ? words.onMeans : words.offMeans} It is recorded on their timeline and can be undone from Batch updates.
        </DialogContentText>
        <TextField
          label={value ? 'How did they agree?' : 'Why (optional)'}
          placeholder={value ? 'Asked on a call, 14 Oct, to get the newsletter' : 'Replied STOP to a text'}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          required={value}
          multiline
          minRows={2}
          inputProps={{ maxLength: 1000 }}
          autoFocus
        />
        {error && <Alert severity="error">{describeBatchError(error)}</Alert>}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 1.5 }}>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={submit}
          disabled={!ready || loading}
          startIcon={loading ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {value ? 'Opt them in' : 'Opt them out'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/**
 * Under the consent gates, beside Mark do not call: opt in or out of emails
 * and texts. Hidden for a merged-away record and for anyone marked do not
 * contact, which stops both whatever their opt-in says.
 */
export const ConsentChange = ({ contact, onChanged }: { contact: ContactDetail; onChanged: () => void }) => {
  const [open, setOpen] = useState<{ field: ConsentField; value: boolean } | null>(null);
  if (contact.mergedIntoId != null || contact.doNotContact) return null;

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1.5 }}>
      {(['emailOptIn', 'smsOptIn'] as const).map((field) => {
        const value = !contact[field];
        return (
          <Button key={field} size="small" variant="outlined" startIcon={WORDS[field].icon} onClick={() => setOpen({ field, value })}>
            {value ? WORDS[field].on : WORDS[field].off}
          </Button>
        );
      })}
      {open && (
        <ConsentDialog
          contactId={contact.id}
          field={open.field}
          value={open.value}
          onClose={() => setOpen(null)}
          onDone={() => {
            setOpen(null);
            onChanged();
          }}
        />
      )}
    </Box>
  );
};
